import { type FC, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoostInstantCompleteMutation } from '@api/queries/boost';
import { setCookie } from '@common/lib/cookie';
import { CoinBalanceTopUp } from '@components/CoinBalanceTopUp/CoinBalanceTopUp';
import { HugeText, TextSmall } from '@components/Typography/Typography';
import { MainButton } from '@components/ui/Button/Button';
import { Checkbox } from '@components/ui/Checkbox/Checkbox';
import { CurrencyImage } from '@components/ui/CurrencyImage/CurrencyImage';
import { addToStorage } from '@redux/reducers/userReducer';
import { useAppDispatch } from '@redux/store';

import { type GoodsType } from '@/common/types/goods';
import { type PlantTypeText } from '@/common/types/plant';

import { ModalContext } from '../../Modal/config';

interface ConfimBuyProps {
  plantId: string;
  material: PlantTypeText | GoodsType;
}

const ConfirmBuy: FC<ConfimBuyProps> = ({ plantId, material }) => {
  const { closeModal, boostProductionDynamicData } = useContext(ModalContext);
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const [completeProduction, { isLoading }] = useBoostInstantCompleteMutation();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const confirmBuy = (): void => {
    if (isChecked) {
      setCookie(
        'askForCompleteProductionPurchase',
        { status: true },
        { secure: true, 'max-age': 86400 },
      );
    }
    completeProduction({ plantId })
      .unwrap()
      .then(({ plant, condition }) => {
        const payload = {
          item: material,
          value: condition.localStock + condition.incommingStorage,
          costPerUnit: plant.costPerUnit,
        };
        dispatch(addToStorage(payload));
      });
    closeModal();
  };

  return (
    <>
      <CurrencyImage className="mb-2.5" size="4.8rem" kind="tgem" />
      <HugeText className="mb-2">{boostProductionDynamicData?.price}</HugeText>
      <TextSmall
        weight="regular"
        className="leading-1.2 max-w-[207px] text-center mb-5"
      >
        {t('Spend {{price}} T-Gems to instantly complete a production queue?', {
          price: boostProductionDynamicData?.price,
        })}
      </TextSmall>
      <div className="flex items-center gap-2 mb-5">
        <Checkbox
          onChange={() => setIsChecked((state) => !state)}
          checked={isChecked}
          variant="default"
          classNames={{
            label: 'flex items-center gap-2',
          }}
          label={() => (
            <TextSmall weight="regular" className="text-white/60">
              {t('Dont ask today')}
            </TextSmall>
          )}
        />
      </div>
      <MainButton
        onClick={confirmBuy}
        disabled={isLoading}
        isLoading={isLoading}
        className="!w-[12.75rem] mb-10 shrink-0"
        text={t('Confirm')}
      />
      <CoinBalanceTopUp compare={boostProductionDynamicData?.price} />
    </>
  );
};

export { ConfirmBuy };
